import { useState, useEffect } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { Portal } from "@/components/common";

const DISMISS_KEY = "iks-install-dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS reports as Mac; detect touch Macs too
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    if (isIOS()) {
      setIos(true);
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setShow(false));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    else dismiss();
    setDeferred(null);
  };

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setShow(false), 15000);
    return () => clearTimeout(t);
  }, [show]);

  if (!show) return null;

  return (
    <Portal>
      <div
        className="fixed inset-x-0 bottom-0 z-[900] p-3 md:p-4 md:left-auto md:right-4 md:bottom-4 md:max-w-sm pointer-events-none"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 5rem)" }}
        data-testid="install-prompt"
      >
        <div className="iks-card iks-pop p-4 flex gap-3 shadow-2xl pointer-events-auto">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center">
            <Download size={20} className="text-sky-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">Add to Home Screen</p>
            {ios ? (
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                Install InfoKeepSake to get reminder alerts on your iPhone. Tap{" "}
                <Share size={13} className="inline -mt-0.5 text-sky-400" /> <b>Share</b>, then{" "}
                <span className="inline-flex items-center gap-0.5">
                  <b>Add to Home Screen</b> <Plus size={12} className="inline text-sky-400" />
                </span>
                .
              </p>
            ) : (
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                Install the app for a full-screen experience and push reminders for dates & bills.
              </p>
            )}
            {!ios && (
              <button
                onClick={install}
                data-testid="install-app-btn"
                className="mt-2.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors"
              >
                Install
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            data-testid="install-dismiss-btn"
            className="shrink-0 self-start text-slate-500 hover:text-slate-200 p-1"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </Portal>
  );
}
